import React, { FocusEvent, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectInputBoxProps {
  id: string;
  label?: string;
  value?: string;
  options: SelectOption[] | string[];
  onChange?: (value: string) => void;
  onFocusChange?: (event: FocusEvent<HTMLButtonElement>) => void;
  onFocus?: (event: FocusEvent<HTMLButtonElement>) => void;
  onBlur?: (event: FocusEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  required?: boolean;
  error?: boolean | string;
  className?: string;
  size?: "sm" | "md" | "lg";
  placeholder?: string;
}

const SelectInputBox: React.FC<SelectInputBoxProps> = ({
  id,
  label,
  value,
  options,
  onChange,
  onFocusChange,
  onFocus,
  onBlur,
  disabled = false,
  required = false,
  error,
  className = "",
  size = "md",
  placeholder
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const hasError = typeof error === "string" ? !!error : !!error;
  const errorMessage = typeof error === "string" ? error : undefined;

  // Normalize options to SelectOption format
  const normalizedOptions: SelectOption[] = options.map((option) => {
    if (typeof option === "string") {
      return { value: option, label: option };
    }
    return option;
  });

  // Find the selected option label
  const selectedOption = normalizedOptions.find((opt) => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : "";

  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "px-3 py-2 text-sm";
      case "lg":
        return "px-4 py-3 text-lg";
      default:
        return "px-4 py-3 text-sm";
    }
  };

  const baseClasses = `
    w-full font-medium text-secondary-900 bg-white border rounded-lg
    transition-all duration-200 ease-in-out cursor-pointer
    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
    disabled:bg-secondary-50 disabled:text-secondary-500 disabled:cursor-not-allowed
    ${getSizeClasses()}
    ${
      hasError
        ? "border-error-300 focus:ring-error-500 focus:border-error-500"
        : "border-secondary-300 hover:border-secondary-400"
    }
    ${isOpen ? "shadow-lg ring-2 ring-primary-500 border-primary-500" : "shadow-sm"}
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const updatePosition = () => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          setPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width
          });
        }
      };

      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue: string) => {
    onChange?.(optionValue);
    setIsOpen(false);
  };

  const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
    onFocusChange?.(e);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
    // Delay blur to allow click events to fire first
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        onFocusChange?.(e);
        onBlur?.(e);
      }
    }, 0);
  };

  const dropdownContent = isOpen && normalizedOptions.length > 0 && (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] mt-1"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`
      }}
    >
      <div className="bg-white border border-secondary-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
        {normalizedOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={option.disabled}
            className={`w-full px-4 py-2 text-left text-sm transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg focus:outline-none ${
              option.disabled
                ? "text-secondary-400 cursor-not-allowed"
                : value === option.value
                  ? "bg-primary-100 text-primary-700 font-semibold"
                  : "text-secondary-700 hover:bg-primary-50 hover:text-primary-700"
            }`}
            onClick={() => !option.disabled && handleSelect(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-secondary-700 mb-2">
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative" ref={containerRef}>
        <button
          id={id}
          type="button"
          onClick={handleToggle}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          className={baseClasses}
        >
          <div className="flex items-center justify-between">
            <span className={displayValue ? "text-secondary-900" : "text-secondary-400"}>
              {displayValue || placeholder || "Select an option"}
            </span>
            <svg
              className={`w-5 h-5 text-secondary-400 transition-transform duration-200 ${
                isOpen ? "transform rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {dropdownContent && createPortal(dropdownContent, document.body)}
      </div>

      {errorMessage && <p className="mt-1 text-sm text-error-600">{errorMessage}</p>}
    </div>
  );
};

export { SelectInputBox };
